import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { delay, switchMap, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { AuthUser, LoginRequest, LoginResponse } from '../models/auth.model';

const STORAGE_KEY = 'auth.session.v1';
const SIMULATED_LATENCY_MS = 600;
const DEFAULT_SESSION_MS = 8 * 60 * 60 * 1000;
const MOCK_USERS_URL = 'mock/users.json';

interface MockUserRecord extends AuthUser {
  password: string;
}

interface MockUsersFile {
  users: MockUserRecord[];
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiUrl}/auth`;

  private readonly session = signal<LoginResponse | null>(this.readStoredSession());

  readonly currentUser = computed<AuthUser | null>(() => this.session()?.user ?? null);
  readonly isAuthenticated = computed(() => this.session() !== null);

  /**
   * Cuando el backend exponga `POST /auth/login`, reemplazar `simulateLogin` por la llamada HTTP.
   */
  login(req: LoginRequest): Observable<LoginResponse> {
    return this.simulateLogin(req).pipe(tap((res) => this.persistSession(res, req.remember)));
  }

  logout(): void {
    this.session.set(null);
    localStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(STORAGE_KEY);
  }

  get token(): string | null {
    return this.session()?.token ?? null;
  }

  private simulateLogin(req: LoginRequest): Observable<LoginResponse> {
    const user = req.user.trim();
    if (!user || !req.password) {
      return throwError(() => new Error('Credenciales incompletas.'));
    }
    return this.http.get<MockUsersFile>(MOCK_USERS_URL).pipe(
      delay(SIMULATED_LATENCY_MS),
      switchMap((file) => {
        const match = file.users.find(
          (u) =>
            u.username.toLowerCase() === user.toLowerCase() && u.password === req.password,
        );
        if (!match) {
          return throwError(() => new Error('Usuario o contraseña incorrectos.'));
        }
        const { password: _pw, ...authUser } = match;
        const response: LoginResponse = {
          token: 'dev.simulated.' + btoa(`${authUser.username}:${Date.now()}`),
          user: authUser,
          expiresAt: Date.now() + DEFAULT_SESSION_MS,
        };
        return of(response);
      }),
    );
  }

  private persistSession(res: LoginResponse, remember: boolean): void {
    this.session.set(res);
    const primary = remember ? localStorage : sessionStorage;
    const secondary = remember ? sessionStorage : localStorage;
    primary.setItem(STORAGE_KEY, JSON.stringify(res));
    secondary.removeItem(STORAGE_KEY);
  }

  private readStoredSession(): LoginResponse | null {
    try {
      const raw = localStorage.getItem(STORAGE_KEY) ?? sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as LoginResponse;
      if (parsed.expiresAt && parsed.expiresAt < Date.now()) {
        localStorage.removeItem(STORAGE_KEY);
        sessionStorage.removeItem(STORAGE_KEY);
        return null;
      }
      return parsed;
    } catch {
      return null;
    }
  }
}
